import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { crmService, CRM_FILTER_KEYS } from '../services/crmService'
import { REPORTING_PERIODS } from '../services/dashboardService'

const PAGE_SIZE = 25

const RECORD_TYPES = [
    { value: '', label: 'All records' },
    { value: 'organisation', label: 'Organisations' },
    { value: 'user', label: 'Users' },
    { value: 'insights_contact', label: 'Insights contacts' },
]

const ACCOUNT_STATUSES = [
    { value: '', label: 'Any status' },
    { value: 'active_trial', label: 'Active trial' },
    { value: 'active_trial_end_scheduled', label: 'Trial ending' },
    { value: 'expired_trial', label: 'Expired trial' },
    { value: 'active_paid', label: 'Active paid' },
    { value: 'active_paid_suspension_scheduled', label: 'Suspension scheduled' },
    { value: 'suspended', label: 'Suspended' },
]

const TRIAL_STATUSES = [
    { value: '', label: 'Any trial status' },
    { value: 'active', label: 'Active trial' },
    { value: 'completed', label: 'Completed trial' },
]

// Boolean filters: URL value 'true' means on.
const TOGGLES = [
    { key: 'incompleteSettings', label: 'Incomplete Tender Settings' },
    { key: 'noMatches', label: 'No matches' },
    { key: 'neverLoggedIn', label: 'Never logged in' },
    { key: 'suspended', label: 'Suspended' },
    { key: 'reactivated', label: 'Historically reactivated' },
    { key: 'activeInPeriod', label: 'Active in period' },
    { key: 'receivingProfileMatches', label: 'Receiving Profile Matches' },
    { key: 'receivingWiderDiscovery', label: 'Receiving Wider Discovery' },
    { key: 'endingWithin7Days', label: 'Trial ending within 7 days' },
]

const ACCOUNT_TYPES = [
    { value: '', label: 'Customer accounts' },
    { value: 'internal', label: 'Internal / test accounts' },
    { value: 'all', label: 'All accounts' },
]

const TYPE_LABEL = { organisation: 'Organisation', user: 'User', insights_contact: 'Insights contact' }

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : 'Never')

export default function Crm() {
    const [params, setParams] = useSearchParams()
    const [state, setState] = useState({ rows: [], total: 0, loading: true, error: null })
    const [searchText, setSearchText] = useState(params.get('search') || '')
    const [exporting, setExporting] = useState(null)
    const [exportError, setExportError] = useState(null)
    const [linkTarget, setLinkTarget] = useState(null)
    const [reloadKey, setReloadKey] = useState(0)

    const filters = {}
    CRM_FILTER_KEYS.forEach((k) => { if (params.get(k)) filters[k] = params.get(k) })
    const page = Math.max(1, parseInt(params.get('page') || '1', 10))
    const activeKey = JSON.stringify(filters) + page + reloadKey

    // Keep the search box in step with URL changes (e.g. dashboard drill-down, back button)
    useEffect(() => { setSearchText(params.get('search') || '') }, [params])

    useEffect(() => {
        let cancelled = false
        setState((s) => ({ ...s, loading: true, error: null }))
        crmService.list({ ...filters, page, pageSize: PAGE_SIZE })
            .then((res) => {
                if (cancelled) return
                if (!res.success) throw new Error('Failed to load records')
                setState({ rows: res.data.rows || [], total: res.data.total || 0, loading: false, error: null })
            })
            .catch((err) => {
                if (!cancelled) setState({ rows: [], total: 0, loading: false, error: err.message || 'Failed to load records' })
            })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey])

    const setFilter = useCallback((key, value) => {
        const next = new URLSearchParams(params)
        if (value) next.set(key, value); else next.delete(key)
        next.delete('page')
        setParams(next)
    }, [params, setParams])

    const goToPage = (p) => {
        const next = new URLSearchParams(params)
        next.set('page', String(p))
        setParams(next)
    }

    const clearAll = () => { setSearchText(''); setParams(new URLSearchParams()) }

    const activeCount = Object.keys(filters).filter((k) => k !== 'period').length
    const totalPages = Math.max(1, Math.ceil(state.total / PAGE_SIZE))

    const handleExport = async (format) => {
        setExporting(format)
        setExportError(null)
        try {
            await crmService.exportFile(filters, format)
        } catch (err) {
            setExportError(err.message || 'Export failed')
        } finally {
            setExporting(null)
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Admin CRM</h1>
                    <p className="text-sm text-admin-muted mt-1">Organisations, associated users and Insights contacts in one place.</p>
                </div>
                <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => handleExport('csv')} disabled={!!exporting}>
                        {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
                    </button>
                    <button className="btn-secondary" onClick={() => handleExport('xlsx')} disabled={!!exporting}>
                        {exporting === 'xlsx' ? 'Exporting...' : 'Export Excel'}
                    </button>
                </div>
            </div>
            {exportError && <div role="alert" className="p-3 text-sm rounded-md bg-red-50 text-status-error border border-red-200">{exportError}</div>}

            {/* Filters */}
            <div className="card-admin p-4 space-y-4">
                <form
                    className="flex flex-wrap gap-3"
                    role="search"
                    onSubmit={(e) => { e.preventDefault(); setFilter('search', searchText.trim()) }}
                >
                    <input
                        type="search"
                        className="input-admin flex-1 min-w-[220px]"
                        placeholder="Search organisation, contact or email"
                        aria-label="Search by organisation, contact or email"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <button type="submit" className="btn-primary">Search</button>
                </form>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Select label="Record type" value={filters.recordType || ''} options={RECORD_TYPES} onChange={(v) => setFilter('recordType', v)} />
                    <Select label="Account status" value={filters.accountStatus || ''} options={ACCOUNT_STATUSES} onChange={(v) => setFilter('accountStatus', v)} />
                    <Select label="Trial status" value={filters.trialStatus || ''} options={TRIAL_STATUSES} onChange={(v) => setFilter('trialStatus', v)} />
                    <Select label="Account type" value={filters.accountType || ''} options={ACCOUNT_TYPES} onChange={(v) => setFilter('accountType', v)} />
                    <Select
                        label="Reporting period"
                        value={filters.period || ''}
                        options={[{ value: '', label: 'Default' }, ...REPORTING_PERIODS.map((p) => ({ value: p.key, label: p.label }))]}
                        onChange={(v) => setFilter('period', v)}
                    />
                </div>

                <fieldset>
                    <legend className="sr-only">Status filters</legend>
                    <div className="flex flex-wrap gap-2">
                        {TOGGLES.map((t) => {
                            const on = filters[t.key] === 'true'
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    aria-pressed={on}
                                    onClick={() => setFilter(t.key, on ? '' : 'true')}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy ${on ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-admin-text border-admin-border hover:border-brand-navy'}`}
                                >
                                    {t.label}
                                </button>
                            )
                        })}
                        {activeCount > 0 && (
                            <button type="button" className="btn-ghost btn-xs" onClick={clearAll}>Clear all filters</button>
                        )}
                    </div>
                </fieldset>
            </div>

            {/* Results */}
            <div className="card-admin overflow-hidden">
                <div className="px-4 py-3 border-b border-admin-border text-sm text-admin-muted" aria-live="polite">
                    {state.loading ? 'Loading...' : `${state.total.toLocaleString()} record${state.total === 1 ? '' : 's'}`}
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-admin-border text-admin-muted">
                            <tr>
                                {['Type', 'Name', 'Contact', 'Email', 'Status', 'Last login', ''].map((h, i) => (
                                    <th key={i} scope="col" className="px-4 py-3 font-bold uppercase text-xs tracking-wider whitespace-nowrap">{h || <span className="sr-only">Actions</span>}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {state.error ? (
                                <tr><td colSpan={7} className="px-4 py-6">
                                    <div role="alert" className="text-status-error text-sm flex items-center gap-3">
                                        {state.error}
                                        <button className="btn-secondary btn-xs" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
                                    </div>
                                </td></tr>
                            ) : state.loading ? (
                                <tr><td colSpan={7} className="px-4 py-6 text-xs text-admin-muted">Loading records...</td></tr>
                            ) : state.rows.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-6 text-sm text-admin-muted">No records match these filters.</td></tr>
                            ) : state.rows.map((r) => (
                                <tr key={`${r.recordType}-${r.id}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap"><span className="badge badge-neutral">{TYPE_LABEL[r.recordType] || r.recordType}</span></td>
                                    <td className="px-4 py-3 font-bold text-brand-navy">
                                        {r.recordType === 'organisation'
                                            ? <Link to={`/crm/organisations/${r.id}`} className="hover:underline">{r.name}</Link>
                                            : r.name}
                                        {r.isInternal && <span className="ml-2 badge badge-warning align-middle">Internal / test</span>}
                                    </td>
                                    <td className="px-4 py-3 text-admin-text">{r.contact || '-'}</td>
                                    <td className="px-4 py-3 text-admin-text">{r.email || '-'}</td>
                                    <td className="px-4 py-3 text-admin-muted text-xs">{r.accountStatus ? r.accountStatus.replace(/_/g, ' ') : '-'}</td>
                                    <td className="px-4 py-3 text-admin-muted text-xs whitespace-nowrap">{r.recordType === 'insights_contact' ? '-' : fmtDate(r.lastLogin)}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        {r.recordType === 'insights_contact' && (
                                            <button className="btn-secondary btn-xs" onClick={() => setLinkTarget(r)}>Link to organisation</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <nav className="flex items-center justify-between px-4 py-3 border-t border-admin-border text-sm" aria-label="Pagination">
                        <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => goToPage(page - 1)}>Previous</button>
                        <span className="text-admin-muted">Page {page} of {totalPages}</span>
                        <button className="btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>Next</button>
                    </nav>
                )}
            </div>

            {linkTarget && (
                <LinkDialog
                    contact={linkTarget}
                    onClose={() => setLinkTarget(null)}
                    onLinked={() => { setLinkTarget(null); setReloadKey((k) => k + 1) }}
                />
            )}
        </div>
    )
}

function Select({ label, value, options, onChange }) {
    return (
        <label className="block text-xs font-bold text-admin-muted uppercase tracking-wider">
            {label}
            <select className="select-admin mt-1 w-full font-semibold normal-case" value={value} onChange={(e) => onChange(e.target.value)}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    )
}

/**
 * Link / merge dialog. The admin must explicitly pick the organisation from search
 * results (name similarity is never used to pre-select or auto-merge), review a
 * preview, tick the confirmation box and submit.
 */
function LinkDialog({ contact, onClose, onLinked }) {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState([])
    const [searching, setSearching] = useState(false)
    const [org, setOrg] = useState(null)
    const [confirmed, setConfirmed] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)

    const search = async (e) => {
        e.preventDefault()
        if (!query.trim()) return
        setSearching(true)
        setError(null)
        try {
            const res = await crmService.list({ recordType: 'organisation', search: query.trim(), pageSize: 10 })
            setResults(res.data?.rows || [])
        } catch (err) {
            setError(err.message || 'Search failed')
        } finally {
            setSearching(false)
        }
    }

    const submit = async () => {
        setSubmitting(true)
        setError(null)
        try {
            await crmService.link({ insightsContactId: contact.id, organisationId: org.id })
            onLinked()
        } catch (err) {
            setError(err.message || 'Link failed')
            setSubmitting(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="link-title">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
                <h2 id="link-title" className="text-lg font-bold text-brand-navy">Link Insights contact to organisation</h2>
                <p className="text-sm text-admin-muted">
                    Similar names alone do not justify a link. Search for the organisation and confirm you have verified this is the same party.
                </p>

                <div className="p-3 rounded-md bg-gray-50 border border-admin-border text-sm">
                    <p className="font-bold text-brand-navy">{contact.name}</p>
                    <p>{contact.email}</p>
                    <p className="text-xs text-admin-muted mt-1">Source: {contact.source || 'Not recorded'}</p>
                    <p className="text-xs text-admin-muted">Consent: {contact.consent || 'Not recorded'}</p>
                </div>

                <form onSubmit={search} className="flex gap-2">
                    <input className="input-admin flex-1" placeholder="Search organisation, contact or email" aria-label="Search organisations" value={query} onChange={(e) => setQuery(e.target.value)} />
                    <button type="submit" className="btn-secondary" disabled={searching}>{searching ? 'Searching...' : 'Search'}</button>
                </form>

                {results.length > 0 && !org && (
                    <ul className="border border-admin-border rounded-md divide-y divide-gray-100 max-h-48 overflow-y-auto">
                        {results.map((r) => (
                            <li key={r.id}>
                                <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-100" onClick={() => { setOrg(r); setConfirmed(false) }}>
                                    <span className="font-bold text-brand-navy">{r.name}</span>
                                    <span className="block text-xs text-admin-muted">{r.contact} · {r.email}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {org && (
                    <div className="space-y-3">
                        <div className="p-3 rounded-md border border-brand-navy/30 bg-blue-50 text-sm">
                            <p className="font-bold text-brand-navy">Preview</p>
                            <p><span className="text-admin-muted">Contact:</span> {contact.name} ({contact.email})</p>
                            <p><span className="text-admin-muted">will be linked to organisation:</span> <strong>{org.name}</strong></p>
                            <p className="text-xs text-admin-muted mt-1">Contact source and consent information are preserved, and the action is written to the audit log.</p>
                            <button type="button" className="text-xs underline text-brand-navy mt-1" onClick={() => { setOrg(null); setConfirmed(false) }}>Choose a different organisation</button>
                        </div>
                        <label className="flex items-start gap-2 text-sm">
                            <input type="checkbox" className="mt-1" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                            I have verified these records belong to the same party and want to link them.
                        </label>
                    </div>
                )}

                {error && <div role="alert" className="p-3 text-sm rounded-md bg-red-50 text-status-error border border-red-200">{error}</div>}

                <div className="flex gap-3 pt-2">
                    <button className="btn-secondary flex-1" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button className="btn-primary flex-1" onClick={submit} disabled={!org || !confirmed || submitting}>
                        {submitting ? 'Linking...' : 'Confirm link'}
                    </button>
                </div>
            </div>
        </div>
    )
}
